import { mediaList } from '@/mocks/media';
import type { MediaItem, MediaType } from '@/types/media';

export interface MediaFilters {
  genres: string[];
  regions: string[];
  yearFrom: number | null;
  yearTo: number | null;
  keyword: string;
}

export type SortKey = 'updated' | 'hot' | 'rating';

export const genreOptions = [
  '动作',
  '喜剧',
  '剧情',
  '科幻',
  '爱情',
  '悬疑',
  '犯罪',
  '冒险',
  '古装',
  '都市',
  '奇幻',
  '恐怖',
  '家庭',
  '校园',
];

export const regionOptions = ['国产', '欧美', '日韩', '港台'];

export const yearOptions: number[] = [
  2025, 2024, 2023,
];

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '更新时间' },
  { key: 'hot', label: '热度' },
  { key: 'rating', label: '评分' },
];

export function getMediaById(id: number | string): MediaItem | undefined {
  const numericId = typeof id === 'string' ? Number(id) : id;
  return mediaList.find((item) => item.id === numericId);
}

export function getMediaByType(type: string | undefined): MediaItem[] {
  if (!type || type === 'all') return mediaList;
  return mediaList.filter((item) => item.type === (type as MediaType));
}

export function applyFilters(items: MediaItem[], filters: MediaFilters): MediaItem[] {
  return items.filter((item) => {
    if (filters.keyword.trim()) {
      const kw = filters.keyword.trim().toLowerCase();
      const hit =
        item.title.toLowerCase().includes(kw) ||
        item.director.toLowerCase().includes(kw) ||
        item.actors.some((a) => a.toLowerCase().includes(kw)) ||
        item.genres.some((g) => g.toLowerCase().includes(kw));
      if (!hit) return false;
    }
    if (filters.genres.length > 0 && !filters.genres.some((g) => item.genres.includes(g))) {
      return false;
    }
    if (filters.regions.length > 0 && !filters.regions.includes(item.region)) {
      return false;
    }
    if (filters.yearFrom && item.year < filters.yearFrom) return false;
    if (filters.yearTo && item.year > filters.yearTo) return false;
    return true;
  });
}

export function sortMedia(items: MediaItem[], sort: SortKey): MediaItem[] {
  const list = [...items];
  if (sort === 'hot') {
    return list.sort((a, b) => b.hot - a.hot);
  }
  if (sort === 'rating') {
    return list.sort((a, b) => b.rating - a.rating);
  }
  return list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function getRelated(item: MediaItem, count = 4): MediaItem[] {
  return mediaList
    .filter((m) => m.id !== item.id)
    .map((m) => {
      const genreScore = m.genres.filter((g) => item.genres.includes(g)).length;
      const typeScore = m.type === item.type ? 1 : 0;
      const regionScore = m.region === item.region ? 1 : 0;
      return { m, score: genreScore * 3 + typeScore + regionScore };
    })
    .sort((a, b) => b.score - a.score || b.m.hot - a.m.hot)
    .slice(0, count)
    .map((entry) => entry.m);
}

export function typeLabel(type: string | undefined): string {
  if (type === 'movie') return '电影';
  if (type === 'tv') return '电视剧';
  return '全部影视';
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}